import { create } from "zustand";
import { WalletController } from "@api/WalletController";
import { ISwapEmulationRequest, ISwapEmulationResponse, ISwapRequest, IPoolData, IAsset } from "@api/IWalletController";
import Cacher from "@utils/cacher";
import { usePair } from "./pair.store";
import { useSwap } from "./swap.store";

interface IPoolsStore {
    pools: IPoolData[];
    emulationResults: ISwapEmulationResponse[] | null;
    isLoading: boolean;
    error: string | null;
    poolUsdPrices: Record<string, { sellPrice: number | null; buyPrice: number | null }>;

    // Actions
    fetchLiquidityPools: (assetIn: IAsset, assetOut: IAsset, swapAmount?: string) => Promise<boolean>;
    calculateSwapRates: (
        sellAssetGuid: string,
        buyAssetGuid: string,
        swapAmount: string,
        setLoadingState?: boolean,
    ) => Promise<ISwapEmulationResponse[] | null>;
    queueRateCalculation: (sellAsset: IAsset, buyAsset: IAsset, swapAmount: string) => void;
    fetchPoolUsdPrices: () => Promise<void>;
    reset: () => void;
}

const cacher = new Cacher<IPoolData[]>("pools.store-cache", 1000 * 60 * 1);

// For debouncing pool rate calculations
let debounceTimeout: NodeJS.Timeout | null = null;
let latestRequest: { sellAsset: IAsset; buyAsset: IAsset; amount: string } | null = null;
let isCalculatingRates = false;

export const usePools = create<IPoolsStore>()((set, get) => ({
    pools: [],
    emulationResults: null,
    isLoading: false,
    error: null,
    poolUsdPrices: {},

    reset: () => {
        // Clear any pending debounce timeout
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            debounceTimeout = null;
        }

        // Reset the request state
        latestRequest = null;
        isCalculatingRates = false;

        // Reset the store state
        set({
            pools: [],
            emulationResults: null,
            isLoading: false,
            error: null,
            poolUsdPrices: {},
        });
    },

    fetchPoolUsdPrices: async () => {
        const { pools } = get();
        if (!pools || pools.length === 0) return;

        // Track TON price across all pools
        let tonUsdPrice: number | null = null;

        // Process each pool to fetch and update USD prices
        const fetchPromises = pools.map(async (pool) => {
            try {
                // First fetch the pair info for this pool
                await usePair.getState().fetchPairInfo(pool.poolAddress);

                // Then get the USD prices using the fetchPairUsdPriceInfo function
                const { sellAssetUsdPrice, buyAssetUsdPrice } = usePair.getState().fetchPairUsdPriceInfo();

                // Track TON price if found in this pool
                if (sellAssetUsdPrice !== null && pool.assetInJettonMasterAddress === "ton") {
                    tonUsdPrice = sellAssetUsdPrice;
                }
                if (buyAssetUsdPrice !== null && pool.assetOutJettonMasterAddress === "ton") {
                    tonUsdPrice = buyAssetUsdPrice;
                }

                // If we got valid prices, update the poolUsdPrices state
                set((state) => ({
                    poolUsdPrices: {
                        ...state.poolUsdPrices,
                        [pool.poolAddress]: {
                            sellPrice: sellAssetUsdPrice,
                            buyPrice: buyAssetUsdPrice,
                        },
                    },
                }));

                // Update swap store with USD prices
                if (sellAssetUsdPrice !== null) {
                    useSwap.getState().setSellAssetUSDPrice(sellAssetUsdPrice);
                }
                if (buyAssetUsdPrice !== null) {
                    useSwap.getState().setBuyAssetUSDPrice(buyAssetUsdPrice);
                }
            } catch (err) {
                console.error("Error fetching pair info for pool", pool.poolAddress, err);
            }
        });

        // Wait for all fetch operations to complete
        await Promise.all(fetchPromises);

        // If we found a TON price in any pool, apply it to all pools that have TON
        if (tonUsdPrice !== null) {
            set((state) => {
                const updatedPoolUsdPrices = { ...state.poolUsdPrices };

                // Update all pools that have TON
                pools.forEach((pool) => {
                    const currentPrices = updatedPoolUsdPrices[pool.poolAddress] || { sellPrice: null, buyPrice: null };

                    // If this pool has TON as the sell asset and price is null, use the found TON price
                    if (pool.assetInJettonMasterAddress === "ton" && currentPrices.sellPrice === null) {
                        updatedPoolUsdPrices[pool.poolAddress] = {
                            ...currentPrices,
                            sellPrice: tonUsdPrice,
                        };
                    }

                    // If this pool has TON as the buy asset and price is null, use the found TON price
                    if (pool.assetOutJettonMasterAddress === "ton" && currentPrices.buyPrice === null) {
                        updatedPoolUsdPrices[pool.poolAddress] = {
                            ...currentPrices,
                            buyPrice: tonUsdPrice,
                        };
                    }
                });

                return { poolUsdPrices: updatedPoolUsdPrices };
            });
        }
    },

    fetchLiquidityPools: async (assetIn: IAsset, assetOut: IAsset, swapAmount?: string) => {
        // Basic validation - both asset GUIDs are required
        if (!assetIn?.guid || !assetOut?.guid) {
            console.error("Missing asset GUIDs for fetching liquidity pools");
            return false;
        }
        // try to find in cache

        const cachedData = cacher.getCachedData();

        if (cachedData && cachedData.length > 0) {

            const neededPools = cachedData.filter(
                (
                    p, // If pool contains both assets - return it
                ) =>
                    p.assetInJettonMasterAddress == assetIn.jettonMasterAddress &&
                    p.assetOutJettonMasterAddress == assetOut.jettonMasterAddress,
            );

            if (neededPools.length > 0) {
                set({ pools: neededPools, isLoading: false });

                // Fetch USD prices for the pools
                get().fetchPoolUsdPrices();

                if (swapAmount) {
                    const numericSwapAmount = parseFloat(swapAmount);
                    if (!isNaN(numericSwapAmount) && numericSwapAmount > 0) {
                        get().queueRateCalculation(assetIn, assetOut, swapAmount);
                    }
                }

                return true;
            }
        }

        set({ isLoading: true, error: null });

        try {
            const poolsResponse = await WalletController.getPools({
                assetInGuid: assetIn.guid,
                assetOutGuid: assetOut.guid,
            } as ISwapRequest);

            if (poolsResponse.status === "error") {
                if (poolsResponse.message === "Requests are limited to 1, every 1000 ms.") {
                    set({ isLoading: false });
                    return false;
                }
                set({ error: "Pools not found", pools: [], isLoading: false });
                console.error("Error fetching pools:", poolsResponse.message);
                return false;
            } else if (poolsResponse.status === "success" && poolsResponse.data) {
                // Store the pools first
                if (cachedData != null) {
                    cacher.setCache([...cachedData, ...poolsResponse.data.pools]);
                } else {
                    cacher.setCache(poolsResponse.data.pools);
                }

                set({ pools: poolsResponse.data.pools, isLoading: false });

                // Fetch USD prices for the pools
                get().fetchPoolUsdPrices();

                // Optionally calculate rates if a swap amount is provided
                if (swapAmount && poolsResponse.data.pools.length > 0) {
                    const numericSwapAmount = parseFloat(swapAmount);
                    if (!isNaN(numericSwapAmount) && numericSwapAmount > 0) {
                        // If we have pools, queue the calculation immediately
                        get().queueRateCalculation(assetIn, assetOut, swapAmount);
                    }
                }

                return poolsResponse.data.pools.length > 0;
            }

            // If we reach here, something went wrong with the response
            set({ isLoading: false });
            return false;
        } catch (error) {
            set({ error: error instanceof Error ? error.message : "Unknown error", isLoading: false });
            console.error("Error in fetchLiquidityPools:", error);
            return false;
        }
    },

    queueRateCalculation: (sellAsset: IAsset, buyAsset: IAsset, swapAmount: string) => {
        latestRequest = { sellAsset, buyAsset, amount: swapAmount };

        if (!isCalculatingRates) {
            set({ isLoading: true, error: null });
        }

        // Clear any existing timeout
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            debounceTimeout = null;
        }

        // Set a new timeout
        debounceTimeout = setTimeout(async () => {
            // If there's already a calculation in progress, don't start another one
            if (isCalculatingRates) {
                return;
            }

            // Check if we have the latest request
            if (!latestRequest) {
                set({ isLoading: false });
                return;
            }

            const { pools } = get();

            // Ensure we have pools before attempting to calculate rates
            if (!pools || pools.length === 0) {
                // Try to fetch pools first, then calculate rates
                const poolsFetched = await get().fetchLiquidityPools(latestRequest.sellAsset, latestRequest.buyAsset);

                if (!poolsFetched) {
                    console.error("Failed to fetch pools for rate calculation");
                    set({ isLoading: false });
                    return;
                }
            }

            // Execute the calculation with the latest request parameters
            isCalculatingRates = true;
            try {
                // Don't set loading state again since we already did it at the start of queueing
                await get().calculateSwapRates(
                    latestRequest.sellAsset.guid,
                    latestRequest.buyAsset.guid,
                    latestRequest.amount,
                    false, // Don't set loading state again
                );
            } finally {
                isCalculatingRates = false;
            }

            // Check if there's a new request that came in while we were calculating
            if (latestRequest && debounceTimeout === null) {
                // If there's a new request but no timeout, schedule another calculation
                get().queueRateCalculation(latestRequest.sellAsset, latestRequest.buyAsset, latestRequest.amount);
            }
        }, 300); // 300ms debounce time
    },

    calculateSwapRates: async (
        sellAssetGuid: string,
        buyAssetGuid: string,
        swapAmount: string,
        setLoadingState = true,
    ) => {
        const { pools } = get();

        if (!pools || pools.length === 0) {
            console.error("No pools available for swap rate calculation");
            return null;
        }

        // Ensure swapAmount is a valid number
        const numericSwapAmount = parseFloat(swapAmount);
        if (isNaN(numericSwapAmount) || numericSwapAmount <= 0) {
            console.error("Invalid swap amount for rate calculation:", swapAmount);
            return null;
        }

        // Only set loading state if requested
        if (setLoadingState) {
            set({ isLoading: true, error: null });
        }

        try {
            // Clear previous results before fetching new ones
            set({ emulationResults: null });

            const response = await WalletController.swapEmulate(
                pools.map((pool) => ({
                    assetInGuid: sellAssetGuid,
                    assetOutGuid: buyAssetGuid,
                    poolAddress: pool.poolAddress,
                    amount: numericSwapAmount,
                })) as ISwapEmulationRequest[],
            );

            if (response.status === "error") {
                set({ error: response.message });
                console.error("Error calculating swap rates:", response.message);
                return null;
            } else if (response.status === "success" && response.data) {
                // Always set emulation results first
                set({ emulationResults: response.data });

                return response.data;
            }
            return null;
        } catch (error) {
            set({ error: error instanceof Error ? error.message : "Unknown error" });
            console.error("Error in calculateSwapRates:", error);
            return null;
        } finally {
            set({ isLoading: false });
        }
    },
}));
